import type { AwningSettings } from "building-types/settings/awningSettings";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { PartialHomeSettings } from "building-types/settings/homeSettings";
import type { InteriorWindowCasingSettings } from "building-types/settings/interiorWindowCasingSettings";
import type {
  AllWindowViewNoteTable,
  AwningViewNoteTable,
  ExteriorWindowCasingViewNoteTable,
} from "building-types/settings/viewNotesSettings";
import type { WindowMaterialSettings } from "building-types/settings/windowMaterialSettings";
import type { WindowMullionSettings } from "building-types/settings/windowMullionSettings";
import type { WindowShutterSettings } from "building-types/settings/windowShutterSettings";
import type { WindowWallFramingSettings } from "building-types/settings/windowWallFramingSettings";
import type {
  MergeSettingsForWindowParams,
  MergeSettingsForGableWindowParams,
} from "building-types/windows/windowMergeTypes";
import type { MergedWindowSettings, WindowSettingsSrc } from "building-types/windows/windowTypes";
import { DEFAULT_INTEGRAL_MULLION_WIDTH } from "common/constants/buildingObjects";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { mergeSettingsForEntity } from "doc/mergeSettingsSet";
import { getDocEntityIdFromGeneratedRoomLayoutEligibleEntity } from "doc/rooms/layout/roomLayoutTools";
import { defaultWindowViewNotes } from "doc/settings/defaultViewNoteSettings";
import { mergeViewNoteTables } from "doc/utils/viewNotesSettingsTools";

export function getMergedWindowSettings(params: MergeSettingsForWindowParams): MergedWindowSettings {
  const openingId = getDocEntityIdFromGeneratedRoomLayoutEligibleEntity(params.window);
  return mergeWindowSettings(params, openingId);
}

export function getMergedGableWindowSettings(params: MergeSettingsForGableWindowParams): MergedWindowSettings {
  assertIsNotUndefined(params.window);
  const openingId = params.window.id;

  return mergeWindowSettings(params, openingId);
}

function mergeWindowSettings(
  {
    window,
    settings,
    descriptor,
    homeSettings,
    optionChoices,
    entitySettings,
    activeSettingsSets,
  }: MergeSettingsForWindowParams | MergeSettingsForGableWindowParams,
  openingId: string | undefined,
) {
  const {
    awning,
    windowFraming,
    windowShutters,
    windowMaterial,
    windowMullions,
    exteriorWindowCasing,
    interiorWindowCasing,
    window: windowSettings,
    windowWrapping,
  } = homeSettings ?? {};

  const mergedTargetedSettings: PartialHomeSettings = {};

  if (window) {
    Object.assign(
      mergedTargetedSettings,
      mergeSettingsForEntity(entitySettings, openingId, activeSettingsSets, settings, optionChoices),
    );
  }

  const mergedMaterialSettings = {
    ...windowMaterial,
    ...descriptor?.materials,
    ...mergedTargetedSettings?.windowMaterial,
    ...window?.materials,
  } as WindowMaterialSettings;

  const mergedMullionSettings = {
    ...windowMullions,
    ...descriptor?.mullions,
    ...mergedTargetedSettings?.windowMullions,
    ...window?.mullions,
  } as WindowMullionSettings;

  const mergedShutterSettings: WindowShutterSettings = {
    ...windowShutters,
    ...descriptor?.shutters,
    ...mergedTargetedSettings?.windowShutters,
    ...window?.shutters,
  };

  const mergedExteriorCasingSettings = {
    ...exteriorWindowCasing,
    ...descriptor?.exteriorCasing,
    ...mergedTargetedSettings?.exteriorWindowCasing,
    ...window?.exteriorCasing,
    viewNotes: mergeViewNoteTables(
      exteriorWindowCasing?.viewNotes,
      descriptor?.exteriorCasing?.viewNotes,
      mergedTargetedSettings.exteriorWindowCasing?.viewNotes,
      window?.exteriorCasing?.viewNotes,
    ) as ExteriorWindowCasingViewNoteTable,
  } as ExteriorWindowCasingSettings;

  const mergedInteriorCasingSettings = {
    ...interiorWindowCasing,
    ...descriptor?.interiorCasing,
    ...mergedTargetedSettings?.interiorWindowCasing,
    ...window?.interiorCasing,
  } as InteriorWindowCasingSettings;

  const mergedAwningSettings: AwningSettings = {
    ...awning,
    ...descriptor?.awningSettings,
    ...mergedTargetedSettings?.awning,
    ...window?.awningSettings,
    viewNotes: mergeViewNoteTables(
      awning?.viewNotes,
      descriptor?.awningSettings?.viewNotes,
      mergedTargetedSettings.awning?.viewNotes,
      window?.awningSettings?.viewNotes,
    ) as AwningViewNoteTable,
  };

  const mergedFramingSettings = {
    ...windowFraming,
    ...descriptor?.framing,
    ...mergedTargetedSettings?.windowFraming,
    ...window?.framing,
  } as WindowWallFramingSettings;

  const mergedWrappingSettings = {
    ...windowWrapping,
    ...descriptor?.wrapping,
    ...mergedTargetedSettings?.windowWrapping,
    ...window?.wrapping,
  };

  const mergedWindowViewNoteTable = mergeViewNoteTables(
    defaultWindowViewNotes,
    descriptor?.viewNotes,
    window?.viewNotes,
  );

  const allWindowViewNoteTable = mergeViewNoteTables(
    mergedWindowViewNoteTable,
    mergedAwningSettings.viewNotes,
    mergedExteriorCasingSettings.viewNotes,
  ) as AllWindowViewNoteTable;

  return {
    mullions: mergedMullionSettings as any as Required<WindowSettingsSrc["mullions"]>,
    shutters: mergedShutterSettings as any as Required<WindowSettingsSrc["shutters"]>,
    exteriorCasing: mergedExteriorCasingSettings as any as Required<WindowSettingsSrc["exteriorCasing"]>,
    interiorCasing: mergedInteriorCasingSettings as any as Required<WindowSettingsSrc["interiorCasing"]>,
    framing: mergedFramingSettings as any as Required<WindowSettingsSrc["framing"]>,
    wrapping: mergedWrappingSettings as any as Required<WindowSettingsSrc["wrapping"]>,
    awningSettings: mergedAwningSettings as any as Required<WindowSettingsSrc["awningSettings"]>,
    materials: mergedMaterialSettings as any as Required<WindowSettingsSrc["materials"]>,
    bugScreen: window?.bugScreen ?? descriptor?.bugScreen,
    verticalDivisionSpacing:
      window?.verticalDivisionSpacing ??
      mergedTargetedSettings?.window?.verticalDivisionSpacing ??
      descriptor?.verticalDivisionSpacing ??
      windowSettings?.verticalDivisionSpacing ??
      DEFAULT_INTEGRAL_MULLION_WIDTH,
    windowHeadHeight: mergedTargetedSettings?.window?.windowHeadHeight,
    operationType: window?.operationType ?? descriptor?.operationType,
    disableAutoDimensions: window?.disableAutoDimensions ?? descriptor?.disableAutoDimensions,
    paneSplitParam: window?.paneSplitParam ?? descriptor?.paneSplitParam,
    paneSplitParamNormalized: window?.paneSplitParamNormalized ?? descriptor?.paneSplitParamNormalized,
    viewNoteTable: allWindowViewNoteTable,
  };
}
